import { Link } from "@frontend/lib/link";
import { BoostTierIcon } from "@frontend/components/boost-tier-icon";
import { CallHeaderButton } from "@frontend/components/collab/call-header-button";
import { CollabActionBox } from "@frontend/components/collab/collab-action-box";
import { CollabRoomFrame } from "@frontend/components/collab/collab-mobile-room";
import { CollabRail } from "@frontend/components/collab/collab-rail";
import { CollabRoomSync } from "@frontend/components/collab/collab-room-sync";
import { CollabRoleBadge } from "@frontend/components/collab-role-badge";
import {
  PartiesCard,
  SharedFilesCard,
  SharedLinksCard,
} from "@frontend/components/collab/details-sections";
import { OfferCard } from "@frontend/components/collab/offer-card";
import { PaymentsPanel } from "@frontend/components/collab/payments-panel";
import { Thread } from "@frontend/components/collab/thread";
import { TrustCard } from "@frontend/components/collab/trust-card";
import { TrackScoreBadge } from "@frontend/components/track-score-badge";
import { Avatar, Callout, Money, Pill } from "@frontend/components/ui";
import { ROLE_LABELS, roleCan } from "@creators/backend/lib/access/roles";
import type { CollabAccess } from "@creators/backend/lib/auth";
import type { CollabListRow, CollabRoom } from "@creators/backend/lib/collabs/queries";
import {
  effectiveCollabStatus,
  statusLine,
  statusPill,
  type CollabStatus,
} from "@creators/backend/lib/collabs/state-machine";
import { formatDate } from "@creators/backend/lib/time";
import { PageSkeleton } from "@frontend/components/page-skeleton";
import { clientApi } from "@frontend/lib/api";
import { collabsCacheKey } from "@frontend/lib/data-cache";
import { useAsync } from "@frontend/lib/use-async";
import { useViewer } from "@frontend/lib/viewer";
import { useEffect } from "react";
import { Navigate, useParams } from "react-router-dom";
import { accountAvatarUrl, creatorAvatarUrl } from "@frontend/lib/avatar";
import { listCounterpartTrack } from "@frontend/lib/reliability";

export function CollabRoomPage() {
  const { id = "" } = useParams<{ id: string }>();
  const { viewer: sessionViewer } = useViewer();
  useEffect(() => { document.title = "Collab room"; }, []);

  const inbox = useAsync(
    async () => {
      if (!sessionViewer || sessionViewer.isAdmin) return [] as CollabListRow[];
      const { collabs } = await clientApi<{ collabs: CollabListRow[] }>("/api/collabs").catch(
        () => ({ collabs: [] as CollabListRow[] }),
      );
      return collabs;
    },
    [sessionViewer?.sessionId, sessionViewer?.isAdmin, sessionViewer?.creator?.id],
    { cacheKey: collabsCacheKey(sessionViewer?.sessionId) },
  );

  const state = useAsync(
    async () => {
      if (!/^[0-9a-f-]{36}$/i.test(id)) return null;
      // Let network errors throw so useAsync keeps the last good room painted.
      const payload = await clientApi<{ room: CollabRoom; access: CollabAccess }>(
        `/api/collabs/${id}`,
      );
      if (!payload?.access) return null;
      return payload;
    },
    [id],
    { cacheKey: /^[0-9a-f-]{36}$/i.test(id) ? `collab-room:${id}` : null },
  );

  // Only the first load shows a skeleton — never again once we have a room.
  if (!state.data) {
    if (state.status === "loading" || state.status === "error") {
      return <PageSkeleton variant="inbox" />;
    }
    return <Navigate to="/collabs" replace />;
  }

  const { room, access } = state.data;
  const collabs = inbox.data ?? [];

  const viewer = access.side;
  const rawStatus = room.collab.status as CollabStatus;
  const status = effectiveCollabStatus(rawStatus, room.milestones);
  const brandName = room.brand.companyName || room.brand.name || room.brand.phone;
  const counterpart = viewer === "creator" ? brandName : room.creator.displayName;
  const counterpartAvatar =
    viewer === "creator"
      ? accountAvatarUrl(room.brandAccount)
      : creatorAvatarUrl(room.creator.id, room.creator.avatarKey);
  const paymentDue = room.milestones.some((m) => m.status === "due");
  const pill = statusPill(status, paymentDue);
  const timer = room.activeTimer;
  // A team member reaches the room through the creator's account, so what they can
  // do here comes from their role rather than from which side they are on.
  const delegated = access.role !== null && access.role !== "owner";
  const canManagePayouts = access.role ? roleCan(access.role, "payouts:manage") : true;
  // Anyone signed in gets the inbox beside the thread; a brand reading the collab
  // from a share link has no list, so for them the room stays two panes.
  const showRail = collabs.length > 0;
  const line =
    status === "completed"
      ? null
      : statusLine(
          status,
          room.collab.pendingParty,
          viewer === "admin" ? "creator" : viewer,
          paymentDue,
        );

  const track =
    viewer === "admin"
      ? null
      : listCounterpartTrack({
          yourRole: viewer,
          payer: room.trust.payer,
          delivery: room.trust.delivery,
        });

  const notices = [
    viewer === "brand" && !access.verified ? (
      <Callout key="unverified" tone="blue" title="You are viewing this with the link">
        <Link
          href={`/login?next=${encodeURIComponent(`/collabs/${id}`)}`}
          className="underline underline-offset-2"
        >
          Sign in with WhatsApp
        </Link>{" "}
        when you are ready to accept or pay.
      </Callout>
    ) : null,
    delegated && !access.canAct ? (
      <Callout
        key="delegated"
        tone="amber"
        title={`You are a ${ROLE_LABELS[access.role!]} on ${room.creator.displayName}'s account`}
      >
        You can read this collab but not act on it or post in the thread.
      </Callout>
    ) : null,
  ].filter(Boolean);

  return (
    <div className="mx-auto flex h-full min-h-0 w-full max-w-6xl flex-1 flex-col px-3 py-2 sm:px-4 sm:py-3 lg:py-4">
      <CollabRoomSync collabId={id} revision={room.revision} onChange={state.reload} />

      <CollabRoomFrame
        rail={
          showRail ? (
            <div className="hidden shrink-0 border-r border-line lg:block lg:w-[17rem]">
              <CollabRail collabs={collabs} activeId={id} />
            </div>
          ) : null
        }
        header={
          <header className="flex shrink-0 items-center gap-2 border-b border-line px-2.5 py-2 sm:gap-3 sm:px-4 lg:px-6">
            <Link
              href="/collabs"
              className={`-ml-0.5 inline-flex size-9 shrink-0 items-center justify-center rounded-lg text-ink-subtle transition hover:bg-canvas hover:text-ink sm:size-10 ${showRail ? "lg:hidden" : ""}`}
            >
              <span className="sr-only">Back to collabs</span>
              <BackIcon />
            </Link>

            <Avatar
              name={counterpart}
              url={counterpartAvatar}
              size="sm"
              className={`sm:size-9 sm:text-xs ${showRail ? "lg:hidden" : ""}`}
            />

            <div className="min-w-0 flex-1">
              <div className="flex min-w-0 items-center gap-1.5">
                <h1 className="truncate text-sm font-semibold tracking-tight text-ink sm:text-base">
                  {counterpart}
                </h1>
                <BoostTierIcon tier={room.collab.boostTier} className="size-4" />
                {track?.value ? (
                  <TrackScoreBadge
                    value={track.value}
                    tone={track.tone}
                    title={`${track.label} · ${track.detail}`}
                  />
                ) : null}
                {viewer !== "admin" ? <CollabRoleBadge role={viewer} /> : null}
              </div>
              <p className="truncate text-[11px] text-ink-subtle sm:text-xs">
                <span className="tabular">{room.collab.reference}</span>
                {line ? (
                  <>
                    <span aria-hidden> · </span>
                    <span>{line}</span>
                  </>
                ) : null}
              </p>
            </div>

            {room.collab.boostTier === "priority" &&
            room.calls &&
            (viewer === "creator" || viewer === "brand") ? (
              <CallHeaderButton
                collabId={id}
                viewer={viewer}
                calls={room.calls}
                canAct={access.canChat && !["expired", "cancelled"].includes(status)}
                viewerPhone={viewer === "creator" ? room.creator.phone : room.brand.phone}
              />
            ) : null}

            <div className="flex max-w-[36%] shrink-0 flex-col items-end gap-0.5 sm:max-w-none">
              {room.currentOffer && room.currentOffer.compensationType !== "barter" ? (
                <Money
                  paise={room.currentOffer.amountTotal}
                  size="sm"
                  className="text-xs text-ink sm:text-sm"
                />
              ) : null}
              <Pill tone={pill.tone} size="sm" className="sm:text-xs sm:leading-normal">
                {pill.label}
              </Pill>
            </div>
          </header>
        }
        chat={
          <>
            {notices.length > 0 ? (
              <div className="shrink-0 space-y-2 border-b border-line px-3 py-2.5 sm:px-4 lg:px-6">
                {notices}
              </div>
            ) : null}
            <Thread
              collabId={id}
              messages={room.messages}
              viewer={viewer}
              creatorName={room.creator.displayName}
              brandName={brandName}
              canPost={access.canChat && !["expired", "cancelled"].includes(status)}
              showReadReceipts={room.collab.boostTier === "priority"}
              counterpartLastReadAt={room.counterpartLastReadAt}
            />
          </>
        }
        details={
          <div className="space-y-3 p-3 pb-10 lg:flex lg:h-full lg:min-h-0 lg:flex-col lg:p-0 lg:pb-0">
            <CollabActionBox
              room={room}
              viewer={viewer}
              verified={access.verified}
              canAct={access.canAct}
              timer={timer}
            />
            <div className="space-y-3 lg:min-h-0 lg:flex-1 lg:overflow-y-auto lg:p-3 lg:pt-4 lg:pb-8 lg:[scrollbar-gutter:stable]">
              {room.currentOffer ? (
                <OfferCard
                  offer={room.currentOffer}
                  changes={room.changes}
                  status={status}
                  pendingParty={room.collab.pendingParty}
                  viewer={viewer}
                  frozen={Boolean(room.terms)}
                  compact
                />
              ) : null}

              <TrustCard room={room} viewer={viewer} />

              <PaymentsPanel room={room} viewer={viewer} canManagePayouts={canManagePayouts} />

              <PartiesCard room={room} viewer={viewer} />
              <SharedFilesCard room={room} />
              <SharedLinksCard room={room} />

              {room.terms ? (
                <p className="px-1 pb-1 text-[11px] leading-relaxed text-ink-subtle">
                  Terms locked on {formatDate(room.terms.acceptedAt)} when the{" "}
                  {room.terms.acceptedBy} accepted.
                </p>
              ) : null}
            </div>
          </div>
        }
      />
    </div>
  );
}

function BackIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="size-5" aria-hidden="true">
      <path
        fillRule="evenodd"
        d="M12.78 5.22a.75.75 0 0 1 0 1.06L9.06 10l3.72 3.72a.75.75 0 1 1-1.06 1.06l-4.25-4.25a.75.75 0 0 1 0-1.06l4.25-4.25a.75.75 0 0 1 1.06 0Z"
        clipRule="evenodd"
      />
    </svg>
  );
}
